import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { DEFAULT_PREFERENCES, themesFor, VISION_MODES, type Preferences, type ThemeDef } from '../../shared/themes';
import { useMe } from '../lib/me';
import { Icon } from '../components/Icon';
import { Badge, Button, PageHeader, Panel, Toggle, useToast } from '../components/ui';

function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: { id: T; label: string }[]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} role="radio" aria-checked={value === o.id} className={value === o.id ? 'active' : ''} onClick={() => onChange(o.id)}>{o.label}</button>
      ))}
    </div>
  );
}

function Row({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <div className="pref-row">
      <div><strong>{title}</strong><span className="muted small">{hint}</span></div>
      <div className="pref-control">{children}</div>
    </div>
  );
}

/** A miniature of the dashboard drawn in a theme's own colors. */
function ThemePreview({ theme, light }: { theme: ThemeDef; light: boolean }) {
  const [bg, panel, accent, second] = light ? theme.swatches.light : theme.swatches.dark;
  const cut = theme.hud ? 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)' : undefined;
  return (
    <div className={`theme-preview ${theme.hud ? 'hud' : ''} ${theme.secret ? 'tp-secret' : ''}`} style={{ background: bg }} aria-hidden="true">
      <div className="tp-side" style={{ background: panel, borderColor: `${accent}55` }}>
        <i style={{ background: accent }} /><i style={{ background: `${accent}66` }} /><i style={{ background: `${accent}66` }} /><i style={{ background: `${accent}66` }} />
      </div>
      <div className="tp-main">
        <div className="tp-hero" style={{ background: panel, borderColor: `${accent}55`, clipPath: cut }}>
          <b style={{ background: second }} /><b style={{ background: accent, width: '60%' }} />
        </div>
        <div className="tp-cards">
          {[0, 1, 2].map((i) => <div key={i} style={{ background: panel, borderColor: `${accent}55`, clipPath: cut }}><span style={{ background: accent }} /></div>)}
        </div>
      </div>
      {theme.hud && !light && <div className="tp-scan" />}
      {theme.emblem && <img className="tp-emblem" src={theme.emblem} alt="" loading="lazy" />}
    </div>
  );
}

export function AppearancePage() {
  const { me, preferences: p, setPreferences } = useMe();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [found, setFound] = useState<Record<string, number>>({});
  const hasExtras = (me?.unlocks ?? []).length > 0;
  useEffect(() => { if (hasExtras) api.get<{ counts: Record<string, number> }>('/api/me/unlocks/stats').then((r) => setFound(r.counts)).catch(() => {}); }, [hasExtras]);
  const set = async (patch: Partial<Preferences>) => {
    setSaving(true);
    try { await setPreferences(patch); } catch (e) { toast((e as Error).message, 'danger'); } finally { setSaving(false); }
  };
  const light = p.mode === 'light' || (p.mode === 'system' && document.documentElement.getAttribute('data-mode') === 'light');
  const isDefault = JSON.stringify(p) === JSON.stringify(DEFAULT_PREFERENCES);

  return (
    <div className="stack">
      <PageHeader eyebrow="Citizen" title="Appearance & accessibility"
        subtitle="Choose how the dashboard looks and reads. Changes apply instantly and follow your account to any device."
        actions={<>{saving && <Badge>saving…</Badge>}<Button variant="ghost" icon="refresh" disabled={isDefault} onClick={() => set(DEFAULT_PREFERENCES)}>Reset to defaults</Button></>} />

      <Panel title="Theme" icon="eye">
        <div className="theme-grid" role="radiogroup" aria-label="Theme">
          {themesFor(me?.unlocks ?? []).map((t) => (
            <button key={t.id} role="radio" aria-checked={p.theme === t.id} className={`theme-card ${p.theme === t.id ? 'active' : ''}`} onClick={() => set({ theme: t.id })}>
              <ThemePreview theme={t} light={light} />
              <div className="theme-meta">
                <strong>{t.name}</strong>
                {t.id === 'ravenbound' && <Badge tone="gold">default</Badge>}
                {t.secret && <Badge tone="blue">found it</Badge>}
                {t.secret && found[t.id] !== undefined && <span className="muted small theme-found" title="Members who have found this so far">{found[t.id] === 1 ? 'only you so far' : `${found[t.id]} found so far`}</span>}
                {p.theme === t.id && <Icon name="check" size={16} className="theme-check" />}
              </div>
              <span className="muted small">{t.description}</span>
            </button>
          ))}
        </div>
        <Row title="Mode" hint="Light or dark versions of every theme. System follows your device setting.">
          <Segmented label="Mode" value={p.mode} onChange={(mode) => set({ mode })}
            options={[{ id: 'dark', label: 'Dark' }, { id: 'light', label: 'Light' }, { id: 'system', label: 'System' }]} />
        </Row>
      </Panel>

      <Panel title="Color vision" icon="eye">
        <p className="muted small">Swaps status colors (success, warning, error) for ones you can tell apart, and adds symbols and shapes so nothing relies on color alone.</p>
        <div className="vision-grid" role="radiogroup" aria-label="Color vision">
          {VISION_MODES.map((v) => (
            <button key={v.id} role="radio" aria-checked={p.vision === v.id} className={`vision-card ${p.vision === v.id ? 'active' : ''}`} onClick={() => set({ vision: v.id })}>
              <div className="vision-head"><strong>{v.name}</strong>{p.vision === v.id && <Icon name="check" size={15} />}</div>
              <span className="muted small">{v.description}</span>
              <div className={`vision-sample vision-${v.id}`} aria-hidden="true"><i className="vs-ok" /><i className="vs-warn" /><i className="vs-bad" /></div>
            </button>
          ))}
        </div>
        <div className="vision-preview" aria-label="Preview with your current setting">
          <span className="muted small">Preview:</span>
          <Badge tone="green">active</Badge><Badge tone="amber">beta</Badge><Badge tone="red">suspended</Badge>
          <span className="dot dot-success" /> <span className="small">success</span>
          <span className="dot dot-failure" /> <span className="small">failure</span>
        </div>
      </Panel>

      <Panel title="Reading & interaction" icon="settings">
        <Row title="Text size" hint="Scales text and the whole layout.">
          <Segmented label="Text size" value={p.textSize} onChange={(textSize) => set({ textSize })}
            options={[{ id: 'sm', label: 'Small' }, { id: 'md', label: 'Default' }, { id: 'lg', label: 'Large' }, { id: 'xl', label: 'Extra large' }]} />
        </Row>
        <Row title="High contrast" hint="Pure black or white backgrounds, solid borders and stronger text.">
          <Toggle label="High contrast" checked={p.contrast === 'high'} onChange={(v) => set({ contrast: v ? 'high' : 'normal' })} />
        </Row>
        <Row title="Readable font" hint="Switches to Atkinson Hyperlegible, a typeface designed for low vision and easier letter recognition.">
          <Toggle label="Readable font" checked={p.font === 'readable'} onChange={(v) => set({ font: v ? 'readable' : 'default' })} />
        </Row>
        <Row title="Motion" hint="Reduce turns off animations and the HUD sweep. System follows your device setting.">
          <Segmented label="Motion" value={p.motion} onChange={(motion) => set({ motion })}
            options={[{ id: 'system', label: 'System' }, { id: 'reduce', label: 'Reduce' }, { id: 'full', label: 'Full' }]} />
        </Row>
        <Row title="Reduce transparency & effects" hint="Solid panels; removes background grid, glows, blur and scanlines.">
          <Toggle label="Reduce transparency" checked={p.transparency === 'reduce'} onChange={(v) => set({ transparency: v ? 'reduce' : 'normal' })} />
        </Row>
        <Row title="Underline links" hint="Makes links in text easy to spot without relying on color.">
          <Toggle label="Underline links" checked={p.links === 'underline'} onChange={(v) => set({ links: v ? 'underline' : 'default' })} />
        </Row>
        <Row title="Enhanced focus outline" hint="A thick yellow outline shows where you are when navigating with the keyboard (Tab).">
          <Toggle label="Enhanced focus outline" checked={p.focus === 'enhanced'} onChange={(v) => set({ focus: v ? 'enhanced' : 'normal' })} />
        </Row>
      </Panel>
    </div>
  );
}
